import type { ChauffeurPage } from "../slug-chauffeur";

import heroImage from "@/assets/images/chauffeur/cote-azur/chauffeur-cote-azur.png";
import { cityPriceFaq } from "./cityPricing";

const laTurbie: ChauffeurPage = {
  slug: "la-turbie",
  title: "Chauffeur privé à La Turbie – VTC & transferts",
  subtitle:
    "Chauffeur privé et VTC à La Turbie : transfert aéroport de Nice, navette Monaco et Grande Corniche au-dessus de la principauté, 7j/7.",
  keywords: [
    "chauffeur privé la turbie",
    "vtc la turbie",
    "chauffeur la turbie",
    "transfert la turbie aéroport nice",
  ],
  hero: {
    title: "Chauffeur privé & VTC à La Turbie",
    subtitle:
      "Le village du Trophée des Alpes, perché au-dessus de Monaco sur la Grande Corniche : transferts aéroport de Nice et mises à disposition.",
    keywords: [
      "VTC La Turbie",
      "chauffeur Grande Corniche",
      "transfert aéroport Nice",
      "chauffeur au-dessus de Monaco",
    ],
    imageSrc: heroImage.src,
    imageAlt: "Chauffeur privé & VTC à La Turbie, au-dessus de Monaco",
    badges: ["Grande Corniche", "Au-dessus de Monaco", "Chauffeurs bilingues"],
  },
  benefits: {
    heading: "Pourquoi un chauffeur à La Turbie",
    items: [
      {
        title: "Perché au-dessus de Monaco",
        description:
          "Le village domine la principauté depuis la Grande Corniche : le chauffeur gère la route en lacets et les accès étroits jusqu’au Trophée d’Auguste.",
      },
      {
        title: "Liaison Monaco en minutes",
        description:
          "La Turbie surplombe Monte-Carlo : descente vers le port, le Casino ou le Rocher, puis remontée le soir sans souci de stationnement.",
      },
      {
        title: "Aéroport de Nice ~35 min",
        description:
          "Rejoignez l’aéroport de Nice Côte d’Azur en environ 35 minutes par la Grande Corniche ou l’A8, avec suivi de vol.",
      },
      {
        title: "Chauffeurs bilingues",
        description:
          "FR/EN, basés sur la Côte d’Azur, ponctuels et discrets — 5,0/5 sur 38 avis Google.",
      },
    ],
  },
  services: {
    heading: "Nos services de chauffeur à La Turbie",
    services: [
      {
        title: "Transfert aéroport de Nice",
        text: "Prise en charge à La Turbie avec suivi de vol, vers et depuis l’aéroport de Nice Côte d’Azur, en environ 35 minutes.",
      },
      {
        title: "Navette Monaco",
        text: "Liaison vers Monaco et Monte-Carlo pour dîners, spectacles ou soirées, avec récupération à l’heure convenue.",
      },
      {
        title: "Mise à disposition",
        text: "À l’heure ou à la journée, un chauffeur dédié pour la Grande Corniche, Èze, Nice ou l’Italie voisine.",
      },
      {
        title: "Visite du Trophée des Alpes",
        text: "Dépose au plus près du Trophée d’Auguste et du vieux village, attente pendant la visite et panorama sur la principauté.",
      },
      {
        title: "Longue distance",
        text: "Trajets toute distance depuis La Turbie : Italie (San Remo, Vintimille), Saint-Tropez, stations de ski, avec véhicule confortable.",
      },
    ],
  },
  locations: {
    heading: "Lieux & points de prise en charge",
    items: [
      {
        name: "Trophée d’Auguste (Trophée des Alpes)",
        description: "Monument romain emblématique dominant Monaco.",
      },
      {
        name: "Village de La Turbie",
        description: "Cœur médiéval, ruelles et belvédères sur la principauté.",
      },
      {
        name: "Grande Corniche",
        description:
          "Route panoramique reliant Nice, La Turbie et le col d’Èze.",
      },
      {
        name: "Tête de Chien",
        description:
          "Promontoire au-dessus de Monaco, vue plongeante sur la côte.",
      },
      {
        name: "Monaco / Monte-Carlo",
        description:
          "Principauté en contrebas, accessible en quelques minutes.",
      },
      {
        name: "Aéroport de Nice",
        description: "À environ 35 minutes par la Grande Corniche ou l’A8.",
      },
    ],
  },
  generic: {
    heading: "La Turbie, balcon romain au-dessus de Monaco",
    subheading:
      "Un village de la Grande Corniche où la route panoramique compte autant que la destination.",
    paragraphs: [
      "Perchée à près de 500 mètres au-dessus de Monaco, La Turbie garde le Trophée d’Auguste, monument romain qui domine la principauté. Entre la Grande Corniche, les lacets et les ruelles du vieux village, le chauffeur vous dépose au plus près du monument et vous reprend sur demande, sans contrainte de stationnement.",
      "La position de La Turbie, juste au-dessus de Monaco, en fait un point d’ancrage idéal : on rejoint le port, le Casino ou le Rocher en quelques minutes, puis on remonte le soir sans se soucier de la circulation ni du parking dans la principauté.",
      "Pour l’aéroport de Nice Côte d’Azur, comptez environ 35 minutes par la Grande Corniche ou l’autoroute A8 selon le trafic ; nos chauffeurs bilingues suivent votre vol et adaptent l’itinéraire en direct.",
    ],
    bullets: [
      "Itinéraire Grande Corniche",
      "Liaison Monaco en minutes",
      "Suivi de vol & devis immédiat",
    ],
  },
  process: {
    heading: "Réserver son chauffeur à La Turbie",
    steps: [
      "Indiquez votre trajet (village, Monaco, aéroport de Nice) et vos horaires.",
      "Nous confirmons le véhicule adapté et le forfait fixe ou la mise à disposition.",
      "Votre chauffeur suit votre vol et vous accueille à l’adresse convenue.",
      "Ajustements en direct selon le trafic sur la Corniche et vos imprévus.",
    ],
  },
  faq: [
    cityPriceFaq("fr", "La Turbie"),
    {
      q: "Combien de temps pour rejoindre l’aéroport de Nice depuis La Turbie ?",
      a: "Comptez environ 35 minutes par la Grande Corniche ou l’autoroute A8 selon le trafic, avec suivi de vol et accueil personnalisé.",
    },
    {
      q: "Assurez-vous les liaisons avec Monaco ?",
      a: "Oui, La Turbie surplombe la principauté : nous assurons les navettes vers Monaco et Monte-Carlo pour dîners, spectacles et soirées, avec récupération à l’heure convenue.",
    },
    {
      q: "Combien de temps à l’avance faut-il réserver ?",
      a: "Réservez au plus tôt en haute saison et lors des grands événements monégasques ; nous traitons aussi les demandes de dernière minute selon disponibilité.",
    },
    {
      q: "Vos chauffeurs parlent-ils anglais ?",
      a: "Oui, nos chauffeurs sont bilingues français/anglais, habitués à une clientèle internationale.",
    },
    {
      q: "Quels véhicules proposez-vous à La Turbie ?",
      a: "Berline Mercedes Classe E et Classe S, van 7 places, SUV et minibus VIP, selon le nombre de passagers et l’occasion.",
    },
    {
      q: "Peut-on visiter le Trophée des Alpes avec chauffeur ?",
      a: "Oui, nous vous déposons au plus près du Trophée d’Auguste et du village, avec attente pendant la visite et panorama sur Monaco, sans souci de stationnement.",
    },
  ],
  relatedLinks: [
    { label: "Voiture avec chauffeur à Nice", href: "/chauffeur/nice" },
    {
      label: "Mise à disposition avec chauffeur Côte d’Azur",
      href: "/chauffeur/cote-d-azur",
    },
    { label: "Réserver un transfert aéroport", href: "/chauffeur/aeroport" },
    { label: "Service de chauffeur à Cannes", href: "/chauffeur/cannes" },
  ],
  cta: {
    title: "Votre chauffeur privé à La Turbie",
    text: "Transfert aéroport de Nice, navette Monaco ou mise à disposition — réponse rapide, 7j/7.",
    primaryHref: "/reservation",
    primaryLabel: "Réserver / demander un devis",
  },
  seo: {
    title: "Chauffeur privé à La Turbie — VTC & transfert aéroport",
    description:
      "Chauffeur privé et VTC à La Turbie : transfert aéroport de Nice en ~35 min, navette Monaco et Grande Corniche. 5,0/5 sur 38 avis. Devis immédiat.",
    canonical: "/chauffeur/la-turbie",
  },
};

export default laTurbie;
